import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Typography } from 'antd';
import FormStepDateTime from '../forms/customerBooking/FormStepDateTime';
import LoadingSpinner from '../LoadingSpinner';
import StatusMessage from '../StatusMessage';
import firebase, {
    db,
    COLLECTION_BOOKINGS,
    COLLECTION_BOOKING_DATES_REF,
} from '../../firebase';
import logger from '../../services/logger';
import {
    convertTimestampToMoment,
    createAmendedBookingData,
    createAmendedDateTimeBookingData,
} from '../../utils';
import { CONTACT_PHONE } from '../../constants';
import mailer from '../../services/mailer';
import lambdaClient from '../../lambdaClient';
import API_KEYS from '../../constants/apiKeys';


const propTypes = {
    onSuccess: PropTypes.func.isRequired,
    docId: PropTypes.string.isRequired,
};

const { Title } = Typography;

const { API_GATEWAY_URL } = API_KEYS;

class AmendDateTimeModal extends Component {
    state = {
        visible: this.props.visible,
        isLoading: false,
        hasError: false,
    };

    componentDidUpdate(prevProps) {
        if (prevProps.visible !== this.props.visible) {
            this.setState({ visible: this.props.visible });
        }
    }

    submitAmendBooking = async ({ bookingDateTime }) => {
        const { data, docId } = this.props;
        const amendedBookingData = createAmendedBookingData(data.date_time, bookingDateTime);

        if (!bookingDateTime) {
            return this.handleError(`Attempted to amend ${docId} with no date time selected`);
        }

        this.setState({ amendedBookingData, isLoading: true });

        try {
            const bookingRef = await db.collection(COLLECTION_BOOKINGS).doc(docId);
            const bookingDateTimeRef = await db.collection(COLLECTION_BOOKING_DATES_REF).doc(data.uuid);

            await bookingRef.update(amendedBookingData);
            await bookingDateTimeRef.update(createAmendedDateTimeBookingData(amendedBookingData));

            this.handleAmendSuccess();
        } catch(e) {
            this.handleError(`Error amending booking for ${data.email_address}: ` + JSON.stringify(e));
        }
    }

    handleError = (error = null) => {
        logger.error(error);

        this.setState({ hasError: true });
    }

    handleAmendSuccess = async () => {
        const { amendedBookingData } = this.state;
        const { onSuccess, data, docId } = this.props;

        const url = `${API_GATEWAY_URL}/mail/create-booking`;

        const payload = mailer.createConfigAmendedBooking({...data, ...amendedBookingData});

        const momentObj = convertTimestampToMoment(amendedBookingData.date_time);

        const handleSuccess = async () => {
            logger.notify(`Booking amended: ${data.uuid} - ${momentObj.format('LLLL')}`);

            await firebase.auth().signOut();
        };

        const handleError = async (e) => {
            const bookingsRef = db.collection(COLLECTION_BOOKINGS).doc(docId);

            await bookingsRef.update({ email_amended_error: true });

            logger.error(`Could not send booking amended confirmation email to ${data.email_address}: ` + JSON.stringify(e));

            await firebase.auth().signOut();
        };

        lambdaClient(url, 'POST', payload, handleSuccess, handleError);

        onSuccess('Thanks! You have successfully amended your booking and will receive a confirmation shortly.');
    }

    renderContent = () => {
        const { isLoading, hasError } = this.state;

        if (isLoading) {
            return <LoadingSpinner />;
        }

        if (hasError) {
            return (
                <StatusMessage
                    variant="error"
                    message={`Sorry, we had trouble updating your booking. Please try again, or contact us on ${CONTACT_PHONE}.`}
                />
            );
        }

        return <FormStepDateTime submitStep={this.submitAmendBooking} />;
    }

    render() {
        const { visible } = this.state;
        const { toggleModal } = this.props;

        return (
            <Modal
                title={null}
                visible={visible}
                onCancel={toggleModal}
                footer={null}
            >
                <Title level={4}>
                    Select a new date & time for your booking
                </Title>
                <br />
                { this.renderContent() }
            </Modal>
        );
    }
}

AmendDateTimeModal.propTypes = propTypes;

export default AmendDateTimeModal;
